import { DITypes, LocalStorageKeys } from "@/core/@types/types";
import { type IStorage } from "@/core/plugins/storage/Storage";
import axios, { type AxiosResponse } from "axios";
import { inject, injectable } from "inversify";
import { mapLegacyValidationError } from "./LegacyValidationError";
import { handleSessionResponse, isAuthenticationRequest, ensureSessionToken } from "./middleware/SessionAuthentication";

type IResponseFinancasDevHttp<T> = {
  statusCode?: number;
  Ok?: boolean;
  Result?: T;
  Data?: T;
  Errors?: string;
  Total?: number;
  Error?: string;
};

export interface IFinancasDevHttp {
  get(
    url: string,
    params?: Record<string, any>
  ): Promise<IResponseFinancasDevHttp<any>>;
  post(
    url: string,
    data: unknown,
    config?: { headers?: Record<string, string> }
  ): Promise<IResponseFinancasDevHttp<any>>;
  patch(url: string, data: unknown): Promise<IResponseFinancasDevHttp<any>>;
  delete(url: string): Promise<IResponseFinancasDevHttp<any>>;
}

export const FinancasDevHttpDi = Symbol("FinancasDevHttpDi");

@injectable()
export default class FinancasDevHttp implements IFinancasDevHttp {
  @inject(DITypes.STORAGE) private storage!: IStorage;

  protected async headers(url: string, method: string) {
    const headers: Record<string, string> = {
      Accept: "application/json, text/plain, */*",
      "X-Requested-With": "XMLHttpRequest"
    };
    if (!isAuthenticationRequest(url)) {
      const token = await ensureSessionToken(this.storage.get(LocalStorageKeys.TokenNewHorizons), method, url);
      this.storage.set(LocalStorageKeys.TokenNewHorizons, token);
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  private async request(
    promise: Promise<AxiosResponse<IResponseFinancasDevHttp<any>>>,
    method: string,
    url: string
  ): Promise<IResponseFinancasDevHttp<any>> {
    try {
      const response = await promise;
      await handleSessionResponse(response.status, method, url, response.headers["x-location"]);

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) await handleSessionResponse(error.response.status, method, url, error.response.headers["x-location"]);
      const legacyError = mapLegacyValidationError(error);
      if (legacyError) return legacyError;

      throw error;
    }
  }

  get = async (
    url: string,
    params?: Record<string, any>
  ): Promise<IResponseFinancasDevHttp<any>> =>
    this.request(
      axios.get(url, {
        headers: await this.headers(url, "GET"),
        withCredentials: true,
        params
      }),
      "GET",
      url
    );

  post = async (
    url: string,
    data: unknown,
    config?: { headers?: Record<string, string> }
  ): Promise<IResponseFinancasDevHttp<any>> =>
    this.request(
      axios.post(url, data, {
        headers: { ...(await this.headers(url, "POST")), ...config?.headers },
        withCredentials: true
      }),
      "POST",
      url
    );

  patch = async (
    url: string,
    data: unknown
  ): Promise<IResponseFinancasDevHttp<any>> =>
    this.request(
      axios.patch(url, data, {
        headers: await this.headers(url, "PATCH"),
        withCredentials: true
      }),
      "PATCH",
      url
    );

  delete = async (url: string): Promise<IResponseFinancasDevHttp<any>> =>
    this.request(
      axios.delete(url, {
        headers: await this.headers(url, "DELETE"),
        withCredentials: true
      }),
      "DELETE",
      url
    );
}
